// Analytics for the concierge chat — the one place its events are defined, so the
// contract stays in a single typed spot rather than scattered trackEvent strings.
//
// Privacy: never send message or reply TEXT — only lengths and bounded metadata
// (feature ids, kinds). Raw conversation content must not reach Segment.

import AnalyticsTracking from "Utils/AnalyticsTracking";

const LOCATION = "concierge_chat";

type OpenTrigger = "user" | "auto";
type CtaType = "nav" | "full_page" | "faq_modal" | "unknown";
type ErrorPhase = "load" | "send";

interface Base {
  domain: string;
}

// Widget mounted — the impression / adoption denominator.
export function trackChatShown({ domain }: Base): void {
  AnalyticsTracking.trackEvent("concierge_chat_shown", { location: LOCATION, domain });
}

export function trackChatOpened({ domain, trigger }: Base & { trigger: OpenTrigger }): void {
  AnalyticsTracking.trackEvent("concierge_chat_opened", { location: LOCATION, domain, trigger });
}

export function trackChatClosed({ domain, trigger }: Base & { trigger: OpenTrigger }): void {
  AnalyticsTracking.trackEvent("concierge_chat_closed", { location: LOCATION, domain, trigger });
}

export function trackMessageSent({ domain, messageLength }: Base & { messageLength: number }): void {
  AnalyticsTracking.trackEvent("concierge_message_sent", { location: LOCATION, domain, message_length: messageLength });
}

// Parent row: one per concierge reply. `recommendation_count` is a scalar so
// per-reply aggregates — including the zero-recommendation dead-end, which emits
// no child rows — need no join. `message_id` joins the per-feature child rows.
export function trackReplyReceived({
  domain,
  messageId,
  replyKind,
  recommendationCount,
}: Base & { messageId: string; replyKind: string; recommendationCount: number }): void {
  AnalyticsTracking.trackEvent("concierge_reply_received", {
    location: LOCATION,
    domain,
    message_id: messageId,
    reply_kind: replyKind,
    recommendation_count: recommendationCount,
  });
}

// A child row: one per recommendation actually rendered under a reply — the
// impression side of per-feature CTR. `feature` is the catalog id (breakdown
// dimension), `position` its rank, `message_id` ties it back to its parent reply.
export function trackRecommendationShown({ domain, messageId, feature, position }: Base & { messageId: string; feature: string; position: number }): void {
  AnalyticsTracking.trackEvent("concierge_recommendation_shown", {
    location: LOCATION,
    domain,
    message_id: messageId,
    feature,
    position,
  });
}

// Reuses the app-wide cta_clicked so concierge CTAs land in existing CTA funnels;
// filter by location: "concierge_chat". `feature` is the clicked catalog id;
// `message_id` (when the click came from a reply's affordance) is the click side
// of per-feature CTR and the join key for per-reply attribution.
export function trackCtaClicked({
  domain,
  feature,
  label,
  type,
  messageId,
}: Base & { feature: string; label?: string; type: CtaType; messageId?: string }): void {
  AnalyticsTracking.trackEvent("cta_clicked", { location: LOCATION, domain, feature, label, type, message_id: messageId });
}

export function trackError({ domain, phase }: Base & { phase: ErrorPhase }): void {
  AnalyticsTracking.trackEvent("concierge_error", { location: LOCATION, domain, phase });
}
